package com.islamicmedia.shared.data.cache

import com.islamicmedia.shared.domain.model.SearchCriteria
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.postgrest.from
import kotlinx.serialization.Serializable
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json

class CacheService(private val client: SupabaseClient) {

    private val json = Json { encodeDefaults = true }

    fun generateCacheKey(title: String, mediaType: String, year: String? = null, info: String? = null): String {
        val normalizedTitle = title.lowercase().replace(Regex("[^a-z0-9\\s]"), "").trim()
        val normalizedType = mediaType.lowercase().trim()
        val normalizedYear = year?.trim() ?: "unknown"
        val infoHash = info?.let {
            it.lowercase().trim().hashCode().toUInt().toString(16)
        } ?: "0"
        return "$normalizedTitle::$normalizedType::$normalizedYear::$infoHash"
    }

    fun generateCriteriaHash(criteria: SearchCriteria): String {
        val normalized = criteria.copy(
            childAgeRanges = criteria.childAgeRanges.sorted(),
            customConcerns = criteria.customConcerns.trim().lowercase()
        )
        val serialized = json.encodeToString(normalized)
        return serialized.hashCode().toUInt().toString(16)
    }

    suspend fun checkContentCache(title: String, mediaType: String, year: String? = null, info: String? = null): MediaContentRow? {
        val cacheKey = generateCacheKey(title, mediaType, year, info)
        return try {
            val row = client.from("media_content")
                .select {
                    filter {
                        eq("cache_key", cacheKey)
                    }
                }
                .decodeSingle<MediaContentRow>()

            println("[CACHE] Content cache HIT for key: $cacheKey")

            // Increment hit_count
            try {
                client.from("media_content")
                    .update({
                        set("hit_count", row.hitCount + 1)
                    }) {
                        filter {
                            eq("cache_key", cacheKey)
                        }
                    }
            } catch (e: Exception) {
                println("[CACHE] Failed to increment content hit_count: ${e.message}")
            }

            row
        } catch (e: Exception) {
            println("[CACHE] Content cache MISS for key: $cacheKey (${e.message})")
            null
        }
    }

    suspend fun saveContent(
        title: String,
        mediaType: String,
        summarizedContent: String,
        searchCount: Int,
        year: String? = null,
        info: String? = null
    ): String? {
        val cacheKey = generateCacheKey(title, mediaType, year, info)
        return try {
            val row = MediaContentRow(
                cacheKey = cacheKey,
                title = title,
                mediaType = mediaType,
                summarizedContent = summarizedContent,
                rawSearchCount = searchCount,
                contentLength = summarizedContent.length
            )
            client.from("media_content")
                .upsert(row) {
                    onConflict = "cache_key"
                }

            // Fetch the id after upsert
            val saved = client.from("media_content")
                .select {
                    filter { eq("cache_key", cacheKey) }
                }
                .decodeSingle<MediaContentRow>()

            println("[CACHE] Saved content for key: $cacheKey, id: ${saved.id}")
            saved.id
        } catch (e: Exception) {
            println("[CACHE] Failed to save content for key: $cacheKey (${e.message})")
            null
        }
    }

    suspend fun checkAnalysisCache(contentId: String, criteriaHash: String): MediaAnalysisRow? {
        return try {
            val row = client.from("media_analysis")
                .select {
                    filter {
                        eq("content_id", contentId)
                        eq("criteria_hash", criteriaHash)
                    }
                }
                .decodeSingle<MediaAnalysisRow>()

            println("[CACHE] Analysis cache HIT for contentId: $contentId, hash: $criteriaHash")

            // Increment hit_count
            try {
                client.from("media_analysis")
                    .update({
                        set("hit_count", row.hitCount + 1)
                    }) {
                        filter {
                            eq("content_id", contentId)
                            eq("criteria_hash", criteriaHash)
                        }
                    }
            } catch (e: Exception) {
                println("[CACHE] Failed to increment analysis hit_count: ${e.message}")
            }

            row
        } catch (e: Exception) {
            println("[CACHE] Analysis cache MISS for contentId: $contentId, hash: $criteriaHash (${e.message})")
            null
        }
    }

    suspend fun saveAnalysis(
        contentId: String,
        criteriaHash: String,
        criteria: SearchCriteria,
        resultJson: String
    ) {
        try {
            val criteriaJson = json.encodeToString(criteria)
            val row = MediaAnalysisRow(
                contentId = contentId,
                criteriaHash = criteriaHash,
                criteriaJson = criteriaJson,
                resultJson = resultJson
            )
            client.from("media_analysis")
                .upsert(row) {
                    onConflict = "content_id,criteria_hash"
                }

            println("[CACHE] Saved analysis for contentId: $contentId, hash: $criteriaHash")
        } catch (e: Exception) {
            println("[CACHE] Failed to save analysis for contentId: $contentId, hash: $criteriaHash (${e.message})")
        }
    }

    suspend fun trackEvent(event: AnalyticsEvent) {
        try {
            client.from("analytics_events").insert(event)
            println("[CACHE] Tracked event: ${event.eventType}")
        } catch (e: Exception) {
            // Fire-and-forget: silently catch all exceptions
        }
    }

    /**
     * Paginate through ALL of the user's analysis events and return one metadata row per unique
     * title (newest event wins). No result_json — this is intentionally lightweight so the caller
     * can show the full title list quickly, then stream result_json in chunks via [fetchEventResults].
     */
    suspend fun fetchAllHistoryMeta(userEmail: String): List<AnalyticsEvent> =
        try {
            fetchAllHistoryMeta(userEmail, withSearchSource = true)
        } catch (e: kotlinx.coroutines.CancellationException) {
            throw e
        } catch (e: Exception) {
            // search_source only marks KIDZONE checks: never let it cost the parent their history.
            println("[HISTORY] meta with search_source failed (${e.message}); retrying without it")
            fetchAllHistoryMeta(userEmail, withSearchSource = false)
        }

    private suspend fun fetchAllHistoryMeta(userEmail: String, withSearchSource: Boolean): List<AnalyticsEvent> {
        val metaColumns = io.github.jan.supabase.postgrest.query.Columns.list(
            listOf("id", "event_type", "title", "media_type", "user_email",
                "score", "cost_usd", "created_at") + (if (withSearchSource) listOf("search_source") else emptyList())
        )
        val all = mutableListOf<AnalyticsEvent>()
        var from = 0L
        val pageSize = 200
        while (true) {
            val page = client.from("analytics_events")
                .select(metaColumns) {
                    filter {
                        eq("user_email", userEmail)
                        eq("event_type", "analysis_complete")
                    }
                    order("created_at", io.github.jan.supabase.postgrest.query.Order.DESCENDING)
                    range(from, from + pageSize - 1)
                }
                .decodeList<AnalyticsEvent>()
            all.addAll(page)
            if (page.size < pageSize) break
            from += pageSize
        }
        // One card per unique title — newest event per title is already first due to DESC ordering.
        return all
            .filter { !it.title.isNullOrBlank() && it.id != null }
            .distinctBy { it.title!!.trim().lowercase() }
    }

    /**
     * Batch-fetch result_json (and key metadata) for specific event IDs. Used to progressively
     * fill in the full [AnalysisResult] for chunks returned by [fetchAllHistoryMeta].
     */
    suspend fun fetchEventResults(ids: List<Long>): List<AnalyticsEvent> {
        if (ids.isEmpty()) return emptyList()
        val anyIds: List<Any> = ids
        return try {
            client.from("analytics_events")
                .select(io.github.jan.supabase.postgrest.query.Columns.list(
                    "id", "event_type", "title", "media_type", "user_email",
                    "score", "cost_usd", "created_at", "result_json"
                )) {
                    filter { isIn("id", anyIds) }
                }
                .decodeList<AnalyticsEvent>()
        } catch (e: Exception) {
            println("[HISTORY] Failed to fetch event results for ids $ids: ${e.message}")
            emptyList()
        }
    }
}
